/**
 * @file src/lib/app-router.ts
 * @exports AppNavigation, getAppRouter, navigateApp, setAppRouter, waitForAppRouter
 * @summary Registers the application router and provides ordered imperative navigation.
 */

import type { AnyRouter } from "@tanstack/react-router";
import type { HistoryState } from "@tanstack/history";

declare module "@tanstack/history" {
	interface HistoryState {
		skipRouteChange?: boolean;
	}
}

export type AppNavigation = {
	hash?: string;
	href?: string;
	params?: unknown;
	replace?: boolean;
	search?: unknown;
	skipRouteChange?: boolean;
	to?: string;
};

let appRouter: AnyRouter | null = null;

/** Returns the initialized application router. */
export const getAppRouter = (): AnyRouter => {
	if (!appRouter) {
		throw new Error("Application router has not been initialized");
	}
	return appRouter;
};

/** Waits for an active route load before another imperative router operation starts. */
export const waitForAppRouter = async (): Promise<void> => {
	const router = getAppRouter();
	if (router.state.status === "pending" && router.latestLoadPromise) {
		await router.latestLoadPromise.catch(() => undefined);
	}
};

/** Navigates after any active route load and annotates URL-only transitions for subscribers. */
export const navigateApp = async ({ skipRouteChange, ...options }: AppNavigation): Promise<void> => {
	const router = getAppRouter();
	await waitForAppRouter();
	await router.navigate({
		...options,
		state: (previous: HistoryState) => ({ ...previous, skipRouteChange: Boolean(skipRouteChange) }),
	} as never);
};

/** Registers the application router created by the Vite entry point. */
export const setAppRouter = (router: AnyRouter): void => {
	appRouter = router;
};
